import { Autocomplete } from '@mui/material';
import { Typography } from '~/components/atoms/index.ts';
import { LocationOnOutlined } from '@mui/icons-material';
import { OverridableStyles } from '~/utils/types.ts';
import { AddressSchema } from '~/components/organisms/CustomerForm/CustomerFormProvider.tsx';
import HookFormTextField from '../../atoms/HookForm/TextField/HookFormTextField.tsx';
import {
  Control,
  Controller,
  ControllerProps,
  FieldValues,
} from 'react-hook-form';
import { ReactElement } from 'react';

export interface AddressFinderOption {
  description: string;
  mainText: string;
  secondaryText?: string;
  data?: AddressSchema;
  placeId?: string;
}

export interface AddressFinderProps<T extends FieldValues>
  extends OverridableStyles {
  options: AddressFinderOption[];
  onSelectAddress: (selectedOption: AddressFinderOption | null) => void;
  onInputChange: (inputValue: string) => void;
  control: Control<T>;
  name: ControllerProps<T>['name'];
  placeholder: string;
  disableClearable?: boolean;
  label?: string;
  size?: 'medium' | 'small';
  dataTestId?: string;
  disabled?: boolean;
}

const AddressFinder = <T extends FieldValues>({
  options,
  onSelectAddress,
  onInputChange,
  styleOverrides,
  control,
  name,
  placeholder,
  disableClearable,
  label,
  size,
  dataTestId,
  disabled,
}: AddressFinderProps<T>): ReactElement => {
  const { width, backgroundColor, ...restStyleOverrides } =
    styleOverrides || {};

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange } }) => {
        return (
          <Autocomplete
            sx={{
              width: width || '300px',
              backgroundColor: backgroundColor || '#FFFFFF',
            }}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.description
            }
            filterOptions={(x) => x}
            options={options}
            autoComplete
            disableClearable={disableClearable}
            disabled={disabled}
            includeInputInList
            filterSelectedOptions
            inputValue={value ?? ''}
            noOptionsText="No addresses"
            onChange={(_, selectedOption: AddressFinderOption | null) => {
              onSelectAddress(selectedOption);
              onChange(
                selectedOption?.data?.streetAddress ||
                  selectedOption?.description,
              );
            }}
            onInputChange={(_, value) => {
              onInputChange(value);
            }}
            isOptionEqualToValue={(option, value) =>
              option.description === value.description
            }
            renderInput={(params) => {
              return (
                <HookFormTextField
                  {...params}
                  fullWidth
                  control={control}
                  name={name}
                  placeholder={placeholder}
                  styleOverrides={restStyleOverrides}
                  label={label}
                  size={size}
                  dataTestId={dataTestId}
                />
              );
            }}
            renderOption={(props, option) => {
              return (
                <li
                  {...props}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  <LocationOnOutlined />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'baseline',
                      margin: '0 0 0 12px',
                    }}
                  >
                    <Typography
                      styleOverrides={{ color: 'text.primary' }}
                      variant="body1"
                    >
                      {option.description}
                    </Typography>
                  </div>
                </li>
              );
            }}
          />
        );
      }}
    />
  );
};

export default AddressFinder;
